import { useMemo, useState } from 'react'

import moment from 'moment'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useUnsafeAsyncData } from 'modules/core/hooks/useAsyncData'
import asyncAction from 'modules/core/lib/asyncAction'
import { stringToDate } from 'modules/core/lib/dates'

import documents from 'modules/documents/store/collections/documents'
import Form from 'modules/timeoff/lib/LeaveUpdateForm'
import leaves from 'modules/timeoff/store/collections/leaves'
import medicalLeaveUpdates from 'modules/timeoff/store/collections/medicalLeaveUpdates'
import MedicalLeaveUpdate from 'modules/timeoff/store/models/MedicalLeaveUpdate'

interface Props {
  leaveUpdateId: number
  onClose: () => void
}
interface ReturnType {
  onSubmit: () => void
  loading: boolean
  isProcessing: boolean
  minEffectiveOn: moment.Moment
  leaveUpdate: MedicalLeaveUpdate | null | undefined
  form: Form
}

const useCreateMedicalLeaveUpdate = ({ leaveUpdateId, onClose }: Props): ReturnType => {
  const [minEffectiveOn, setMinEffectiveOn] = useState(moment())
  const [leaveUpdate, setLeaveUpdate] = useState<MedicalLeaveUpdate | null>(null)
  const form = useMemo(() => new Form({}), [])

  const { loading } = useUnsafeAsyncData(async () => {
    let model = medicalLeaveUpdates.find({
      id: leaveUpdateId,
    })

    if (!model) {
      model = new MedicalLeaveUpdate({
        id: leaveUpdateId,
      })
      await model.fetch()
    }

    if (
      medicalLeaveUpdates.find({
        id: leaveUpdateId,
      })
    ) {
      await Promise.all([
        leaves.fetch({
          data: {
            id: model.attributes.leave_id,
          },
        }),
        documents.fetch({
          data: {
            leave_id: model.attributes.leave_id,
          },
        }),
      ])
      setMinEffectiveOn(stringToDate(model.leave.attributes.start_on, 'backend'))
      form.setValues(model.toJS())
      setLeaveUpdate(model)
    } else {
      onClose()
    }
  }, [form, leaveUpdateId, onClose])

  const { running: isProcessing, performAction: onSubmit } = useAsyncAction(async () => {
    const { success } = await asyncAction(async () => {
      if (!leaveUpdate) return

      await form.save(leaveUpdate)

      const year = moment().year()

      return leaves.fetch({
        data: {
          year,
        },
      })
    })

    if (success) onClose()
  })

  return {
    loading,
    onSubmit,
    leaveUpdate,
    isProcessing,
    minEffectiveOn,
    form,
  }
}

export default useCreateMedicalLeaveUpdate
